import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { ConfirmDialog } from "src/ui/containers/dashboard/devices/components/shared/ConfirmDialog";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { ControlledSelectField } from "src/ui/shared/form/SelectField/SelectField";
import { TextField, UncontrolledTextField } from "src/ui/shared/form/TextField";
import { MinimalTable, MinimalTableHeader, MinimalTableHeaderCell } from "src/ui/shared/table/MinimalTableComponents";
import { Header } from "src/ui/shared/table/TableHeader";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { useDeleteOauthClient } from "@admin/hooks/useDeleteOauthClient";
import { useGetOauthClients } from "@admin/hooks/useGetOAuthClients";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { Box, DialogContentText, Paper, TableContainer, TableRow } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { TableBody } from "@shared/table/TableBody";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";

import { useCreateOauthClient } from "../../hooks/useCreateOauthClient";

import { ActionCell } from "./ActionCell";
import { useStyles } from "./AdminOauthClientPage.style";
import { columns, parseLines } from "./AdminOauthClientPage.utils";
import { ClientValidationSchema } from "./AdminOauthClientPage.validation";
import EditClientModal from "./EditClientModal";

type FormValues = API.OauthClient;

const initialValues: FormValues = {
    clientId: "",
    displayName: "",
    clientType: "public",
    clientSecret: null,
    requirements: null,
    redirectUris: ["https://emteria.com/logout"],
    postLogoutRedirectUris: ["https://emteria.com/login"],
    permissions: ["ept:authorization"],
};

const AdminOauthClientPage = () => {
    const { data, isLoading } = useGetOauthClients();
    const { data: createdClient, isLoading: isCreating, onMutate: onCreate } = useCreateOauthClient();
    const { isLoading: isDeleting, onMutate: onDeleteClient } = useDeleteOauthClient();

    const [clients, setClients] = React.useState<API.OauthClient[]>([]);
    const [editingClientId, setEditingClientId] = React.useState<string | null>(null);
    const [deletedClientId, setDeletedClientId] = React.useState<string | null>(null);

    const { classes } = useStyles();
    const { t } = useTranslation(["common", "pages", "modals"]);
    const validate = useValidationSchema(ClientValidationSchema);

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const clientId = values.clientId.trim();
        onCreate({ ...values, clientId }, form.restart);
    };

    const table = useReactTable<API.OauthClient>({
        data: clients,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    React.useEffect(() => {
        setClients(data);
    }, [data]);

    React.useEffect(() => {
        if (createdClient) {
            setClients((prev) => [...prev, createdClient]);
        }
    }, [createdClient]);

    const isTableLoading = isLoading || isCreating || isDeleting;

    return (
        <PageContainer>
            <Form<FormValues>
                onSubmit={onSubmit}
                initialValues={initialValues}
                validate={validate}
                render={({ handleSubmit, submitting, values, form, errors }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminOauthClientsPage.addClient")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <TextField name="clientId" label={t("pages:adminOauthClientsPage.clientId")} />
                                <TextField name="displayName" label={t("common:label.displayName")} />
                                <ControlledSelectField
                                    name="clientType"
                                    label={t("pages:adminOauthClientsPage.clientType")}
                                    options={[
                                        { value: "public", label: t("pages:adminOauthClientsPage.clientTypeOption.public") },
                                        {
                                            value: "confidential",
                                            label: t("pages:adminOauthClientsPage.clientTypeOption.confidential"),
                                        },
                                    ]}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.redirectUris}
                                    value={values?.redirectUris?.join("\n")}
                                    onChange={(e) => form.change("redirectUris", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.redirectUris")}
                                    helperText={t("pages:adminOauthClientsPage.urisHelperText")}
                                    rows={3}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.postLogoutRedirectUris}
                                    value={values?.postLogoutRedirectUris?.join("\n")}
                                    onChange={(e) => form.change("postLogoutRedirectUris", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.postLogoutRedirectUris")}
                                    helperText={t("pages:adminOauthClientsPage.urisHelperText")}
                                    rows={3}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.permissions}
                                    value={values?.permissions?.join("\n")}
                                    onChange={(e) => form.change("permissions", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.permissions")}
                                    helperText={t("pages:adminOauthClientsPage.permissionsHelperText")}
                                    rows={3}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.requirements}
                                    value={values?.requirements?.join("\n")}
                                    onChange={(e) => form.change("requirements", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.requirements")}
                                    helperText={t("pages:adminOauthClientsPage.permissionsHelperText")}
                                    rows={3}
                                />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <PrimaryButton
                                    type="submit"
                                    disabled={submitting || isCreating}
                                    loading={submitting || isCreating}
                                >
                                    {t("common:button.add")}
                                </PrimaryButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />

            <SectionHeader>{t("pages:adminOauthClientsPage.existingClients")}</SectionHeader>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                                <MinimalTableHeaderCell width={80} />
                            </TableRow>
                        ))}
                    </MinimalTableHeader>

                    <TableBody
                        table={table}
                        loading={isTableLoading}
                        noDataMessage={t("pages:adminOauthClientsPage.noClientsToDisplay")}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <TableRow key={row.id}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                                <ActionCell
                                    clientId={row.original.clientId}
                                    isDeleting={isDeleting}
                                    onEdit={() => setEditingClientId(row.original.clientId)}
                                    onDelete={() => setDeletedClientId(row.original.clientId)}
                                />
                            </TableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>

            {editingClientId ? (
                <EditClientModal
                    open
                    clientId={editingClientId}
                    onClose={() => setEditingClientId(null)}
                    onSuccess={(updatedClient: API.OauthClient) => {
                        setClients((prev) =>
                            prev.map((c) => (c.clientId === updatedClient.clientId ? { ...c, ...updatedClient } : c)),
                        );
                    }}
                />
            ) : null}

            {deletedClientId ? (
                <ConfirmDialog
                    open
                    title={t("pages:adminOauthClientsPage.deleteClient")}
                    dangerButton
                    primaryActionText={t("common:button.delete")}
                    onClose={() => setDeletedClientId(null)}
                    onConfirm={() => {
                        onDeleteClient(deletedClientId, (clientId) => {
                            setClients((prev) => prev.filter((c) => c.clientId !== clientId));
                        });
                    }}
                >
                    <DialogContentText>{t("modals:adminDeleteClient.description")}</DialogContentText>
                </ConfirmDialog>
            ) : null}
        </PageContainer>
    );
};

export default withRequiredRole(
    withMetaDescription(AdminOauthClientPage, {
        title: i18next.t("pages:adminOauthClientsPage.metaTitle"),
        description: i18next.t("pages:adminOauthClientsPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
